import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { useChatsStore } from "@/store/useChatsStore";
import { ReadDto } from "@saysimple-platform/dtos";
import { ref } from "@vue/composition-api";
import { storeToRefs } from "pinia";
import { useToast } from "vue-toastification/composition";

const isReadPending = ref(false);

export default function useReadChat() {
    const { publicApiEndpointPath } = useInboxModuleType();
    const toast = useToast();
    const chatsStore = useChatsStore();
    const { isUpdatingChatId } = storeToRefs(chatsStore);
    const { addUnreadChat, deleteUnreadChat } = useUnreadChats();

    const read = (chatId: number, options: { showToast: boolean } = { showToast: true }): Promise<ReadDto> => {
        isUpdatingChatId.value = chatId;
        isReadPending.value = true;

        chatsStore.setReadStatus(chatId, true);
        deleteUnreadChat(chatId);

        return axios
            .post<ReadDto>(`${publicApiEndpointPath.value}/read`, { chatId })
            .then(({ data }) => {
                if (options.showToast) {
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.conversation.mark-as-read"),
                            text: i18n.t("entities.conversation.set-read"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                }

                return data;
            })
            .catch((error) => {
                if (options.showToast) {
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.conversation.mark-as-read"),
                            text: i18n.t("entities.conversation.could-not-set-read"),
                            icon: "times",
                            variant: "error",
                        },
                    });
                }

                return error;
            })
            .finally(() => {
                isUpdatingChatId.value = undefined;
                isReadPending.value = false;
            });
    };

    const unread = (chatId: number): Promise<ReadDto> => {
        isUpdatingChatId.value = chatId;
        isReadPending.value = true;

        chatsStore.setReadStatus(chatId, false);

        return axios
            .post<ReadDto>(`${publicApiEndpointPath.value}/unread`, { chatId })
            .then(({ data }) => {
                addUnreadChat(data);

                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.mark-as-read"),
                        text: i18n.t("entities.conversation.set-read"),
                        icon: "check",
                        variant: "success",
                    },
                });

                return data;
            })
            .catch((error) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.mark-as-read"),
                        text: i18n.t("entities.conversation.could-not-set-read"),
                        icon: "times",
                        variant: "error",
                    },
                });

                return error;
            })
            .finally(() => {
                isUpdatingChatId.value = undefined;
                isReadPending.value = false;
            });
    };

    return {
        read,
        unread,
        isReadPending,
    };
}
